import { TranslateService } from '@ngx-translate/core';
import { Model, ModelOrigin } from 'src/app/interfaces/api/model';
import { LatLon } from 'src/app/interfaces/shared/latLon';
import { XYZ } from 'src/app/interfaces/shared/xyz';
import { PointKey } from 'src/app/interfaces/viewer/origin';
import { ModelService } from 'src/app/services/api/models/model.service';
import { Config } from '../../../../interfaces/shared/config';
import { CalibratorTool } from './CalibratorTool';

const Autodesk = window.Autodesk;

interface CalibratorDockingPanelOptions {
    translate: TranslateService;
    viewer: Autodesk.Viewing.GuiViewer3D;
    model: Model;
    updateModel: (model: Model) => void;
    updateOrigin: (latLon: LatLon, projectLatLonXY?: [number, number] | null) => Model;
    modelService: ModelService;
    onOriginChange: (evt: ModelOrigin, save?: boolean) => void;
    updateOriginPanel: (origin: ModelOrigin) => void;
}

export class CalibratorDockingPanel extends Autodesk.Viewing.UI.DockingPanel {
    viewer: Autodesk.Viewing.GuiViewer3D;
    options: CalibratorDockingPanelOptions;
    translate: TranslateService;
    declare container: HTMLElement;

    currentBBox: any;
    currentPolygon: any;
    calibratorTool!: CalibratorTool;
    currentZone: any;
    currentZones: any[] = [];
    fragIds: number[] = [];
    dbIds: number[] = [];
    currentBBoxes: any;

    fromObjectPanel: any;
    groupeZones: boolean = false;
    colors = Config.colors;
    model: Model;
    origin: ModelOrigin;
    offset: XYZ = { x: 0, y: 0, z: 0 };
    originExt: any; // OriginExtension when implemented
    instanceTree: any;
    geometry: any;
    geometries: any;
    e: any;
    updateOrigin: (latLon: LatLon, projectLatLonXY?: [number, number] | null) => Model;
    updateModel: (model: Model) => void;
    modelService: ModelService;
    onOriginChange: (evt: ModelOrigin, save?: boolean) => void;
    updateOriginPanel: (origin: ModelOrigin) => void;
    previousState: any;

    constructor(viewer: Autodesk.Viewing.GuiViewer3D, container: HTMLElement, id: string, title: string, options: CalibratorDockingPanelOptions) {
        super(container, id, title, options);

        this.viewer = viewer;
        this.options = options;

        this.translate = options.translate;
        this.model = options.model;
        this.origin = this.model.origin;
        this.updateOrigin = options.updateOrigin;
        this.updateModel = options.updateModel;
        this.modelService = options.modelService;
        this.onOriginChange = options.onOriginChange;
        this.updateOriginPanel = options.updateOriginPanel;
        let offset = this.viewer.model.getData().globalOffset;

        if (offset)
            this.offset = offset;

        this.init();
        this.container.classList.add('docking-panel-container-solid-color-a');
        this.container.style.top = "10px";
        this.container.style.right = "3vw";
        this.container.style.width = "364px";
        this.container.style.height = "580px";

        const styleId = 'calibrator-style';

        if (!document.getElementById(styleId)) {
            const link = document.createElement('link');
            link.id = styleId;
            link.rel = 'stylesheet';
            link.href = 'viewer/origin/calibrator/calibrator.css';
            document.head.appendChild(link);
        }

        const div = document.createElement('div');
        div.style.margin = '0';
        div.style.maxHeight = 'calc(100% - 70px)';
        div.style.overflowY = 'auto';

        this.loadAndRenderHtml(div).then(() => {
            this.container.appendChild(div);

            let markerOptions = {
                ...this.options,
                updateModel: this.updateModel,
                viewer: this.viewer,
                updateOriginPanel: this.updateOriginPanel,
                translate: this.translate
            }

            this.calibratorTool = new CalibratorTool(markerOptions);

            this.viewer.toolController.registerTool(this.calibratorTool);

            let addPointBtns = document.querySelectorAll('[id*="add-point-"]') as NodeListOf<HTMLButtonElement>;

            addPointBtns.forEach(btn => {
                let name: PointKey = btn.id.slice(4) as PointKey;
                let updateInputs: any = document.querySelectorAll(`[data-name="${name}"] input.update-pos`);

                btn.addEventListener('click', () => {
                    this.viewer.toolController.activateTool(this.calibratorTool.toolName);
                    this.calibratorTool.setCurrentMarker(name);
                    this.calibratorTool.undoMark("real" as PointKey);

                    let pointImg = document.querySelector(`[id='editor.${name}']`) as HTMLImageElement;
                    let dataName = pointImg.getAttribute("data-name");
                    let inputsEmpty = true;
                    let showsAddOrEdit = dataName == `add-${name}` || dataName == `edit-${name}`;
                    let addTitle = this.translate.instant(name == "point-1" ? 'viewer.add_point' : 'viewer.add_second_point')

                    updateInputs.forEach((input: HTMLInputElement) => {
                        if (input.value)
                            inputsEmpty = false;
                    });

                    let addOrEdit = inputsEmpty ? "add" : "edit";

                    if (showsAddOrEdit) {
                        pointImg.setAttribute("data-name", `validate-${name}`);
                        btn.title = this.translate.instant("viewer.validate");
                        pointImg.src = '/img/viewer/ok.png';
                        btn.classList.add("target");

                        this.calibratorTool.setState(name, "local");
                        this.calibratorTool.updateInput(name, "local");
                        this.calibratorTool.validate(name, false);
                    } else if (dataName == `validate-${name}`) {
                        pointImg.setAttribute("data-name", `${addOrEdit}-${name}`);
                        btn.title = inputsEmpty ? addTitle : this.translate.instant("form.edit");
                        pointImg.src = `/img/viewer/${addOrEdit}.${inputsEmpty ? "svg" : "png"}`;
                        btn.classList.remove("target");

                        if (!inputsEmpty) {
                            this.calibratorTool.setState(name, "real");
                            this.calibratorTool.validate(name, true);
                            this.calibratorTool.updateInput(name, "real");
                        } else {
                            this.calibratorTool.setState(name, "local");
                            this.calibratorTool.validate(name, false);
                            this.calibratorTool.updateInput(name, "local");
                        }
                    }
                });
            })

            let updateInputs: any = document.getElementsByClassName('update-pos');

            if (updateInputs)
                for (let i = 0; i < updateInputs.length; i++) {
                    let cell = updateInputs[i]
                    let dataName = cell.parentNode.getAttribute("data-name");

                    cell.addEventListener('change', (event: Event) => {
                        if (this.viewer.toolController.getActiveTool()?.getName() !== this.calibratorTool.toolName) // TO CHECK
                            this.viewer.toolController.activateTool(this.calibratorTool.toolName);

                        if (dataName == "point-2" && this.calibratorTool.currentMarkerName == "real")
                            this.calibratorTool.setCurrentMarker("real");
                        else
                            this.calibratorTool.setCurrentMarker(dataName);

                        this.calibratorTool.onInputChange(event);
                    });
                }

            let closeBtn = document.querySelector('#calibratorDockingPanel .docking-panel-close');

            closeBtn?.addEventListener('click', () => {
                this.onClose();
            });
        });
    }

    init() {
        this.originExt = this.viewer.loadedExtensions['OriginExtension'];
        this.originExt?.dockingPanel?.setVisible(false);

        if (!this.instanceTree)
            this.instanceTree = this.viewer.model.getData().instanceTree;

        this.previousState = this.viewer.getState();

        if (!this.viewer.model.getData().is2d) {
            this.viewer.setBackgroundColor(255, 255, 255, 255, 255, 255);

            let viewcuiext = this.viewer.getExtension('Autodesk.ViewCubeUi') as any;
            viewcuiext.setViewCube('top');

            this.viewer.loadExtension('Autodesk.NPR').then(() => {
                let viewNPRext = this.viewer.getExtension('Autodesk.NPR') as any;
                viewNPRext.setParameter("style", "graphite");

                viewNPRext.setParameter("brightness", 1.0);
                this.viewer.navigation.toOrthographic();
            })
        }

    }

    reset() {
        let btns = document.querySelectorAll(".add-point-btn");
        btns.forEach((btn) => {
            btn.classList.remove("active");
            (btn as HTMLButtonElement).disabled = false;
        })
    }

    getState(dataName: string = "point-1") {
        let btn = document.querySelector(`[data-name="${dataName}"]`);

        if (!btn) return null;

        return btn.getAttribute("data-state");
    }

    onClose() {
        this.setVisible(false);

        if (!this.calibratorTool.calibrated) {
            this.onOriginChange(this.calibratorTool.initialOrigin);
            this.calibratorTool.updateModel({ ...this.model, origin: this.calibratorTool.initialOrigin });
        }

        this.originExt?.init();
        this.originExt?.dockingPanel?.setVisible(true);
        this.calibratorTool.localPoints = {};

        if (!this.viewer.model.getData().is2d) {
            let viewNPRext = this.viewer.getExtension('Autodesk.NPR') as any;
            viewNPRext.setParameter("style", "");
        }

        this.calibratorTool.removeMarks();
        this.viewer.toolController.deactivateTool(this.calibratorTool.toolName);
        this.viewer.restoreState(this.previousState);
        let originBtn = document.querySelector(".adsk-button-icon.origin-btn-icon") as HTMLDivElement;
        originBtn?.click();
        this.reset();
    }

    async loadAndRenderHtml(div: HTMLDivElement) {
        let template = await this.loadHtmlTemplate('viewer/origin/calibrator/calibrator.html');

        const variables = {
            geometry_point: this.translate.instant('geometry.point'),
            geometry_position: this.translate.instant('geometry.position'),
            geometry_angle_scale: this.translate.instant('geometry.angle_scale'),
            geometry_longitude: this.translate.instant('geometry.longitude'),
            geometry_latitude: this.translate.instant('geometry.latitude'),
            viewer_z: this.translate.instant('viewer.z'),
            viewer_add_point: this.translate.instant('viewer.add_point'),
            viewer_add_second_point: this.translate.instant('viewer.add_second_point'),
            geometry_scale: this.translate.instant('geometry.scale'),
            geometry_angle: this.translate.instant('geometry.angle'),
            viewer_reinit: this.translate.instant('viewer.reinit'),
            viewer_calibrate: this.translate.instant('viewer.calibrate'),
            viewer_done: this.translate.instant('viewer.done'),
            origin_scale: this.origin.scale,
            origin_angle: this.origin.angle,
        };

        let html = this.renderTemplate(template, variables);

        div.innerHTML = html;
    }

    async loadHtmlTemplate(path: string): Promise<string> {
        const response = await fetch(path);
        return await response.text();
    }

    renderTemplate(template: string, variables: Record<string, string | number>) {
        return template.replace(/{{(.*?)}}/g, (_, key) => String(variables[key.trim()] ?? ''));
    }
}
